import "./ConfigureBoardStates.scss";
import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import ColorPicker from "../../../../components/ColorPicker/ColorPicker";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const DEFAULT_STATUS_CONFIG = [
  { label: "Not Started", color: "#797e93", order: 0 },
  { label: "In Progress", color: "#fdbc65", order: 1 },
  { label: "Completed", color: "#34d491", order: 2 },
  { label: "Stuck", color: "#e8697d", order: 3 },
  { label: "Pending", color: "#2382e4", order: 4 },
];

const DEFAULT_PRIORITY_CONFIG = [
  { label: "Urgent", color: "#e8697d", order: 0 },
  { label: "High", color: "#fdbc65", order: 1 },
  { label: "Medium", color: "#2382e4", order: 2 },
  { label: "Low", color: "#34d491", order: 3 },
  { label: "Trivial", color: "#797e93", order: 4 },
];

function SortableItem({ id, item, onUpdate, onRemove, canRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const colorPickerRef = useRef(null);
  const previewRef = useRef(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      ) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showColorPicker]);

  const handlePreviewClick = () => {
    if (previewRef.current) {
      const rect = previewRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setShowColorPicker(!showColorPicker);
  };

  return (
    <div ref={setNodeRef} style={style} className="config-item">
      <div className="drag-handle" {...attributes} {...listeners}>
        <span className="material-symbols-outlined">drag_indicator</span>
      </div>

      <input
        type="text"
        className="config-input"
        value={item.label}
        onChange={(e) => onUpdate(item.order, { label: e.target.value })}
        placeholder="Label"
        maxLength={30}
      />

      <div className="color-picker-container" ref={colorPickerRef}>
        <div
          ref={previewRef}
          className="config-color-preview"
          style={{ backgroundColor: item.color }}
          onClick={handlePreviewClick}
        />

        {showColorPicker && (
          <div
            className="color-palette-dropdown"
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <ColorPicker
              selectedColor={item.color}
              onColorSelect={(color) => {
                onUpdate(item.order, { color });
                setShowColorPicker(false);
              }}
            />
          </div>
        )}
      </div>

      {canRemove && (
        <button
          className="config-remove"
          onClick={() => onRemove(item.order)}
          title="Remove"
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      )}
    </div>
  );
}

SortableItem.propTypes = {
  id: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
};

function ConfigureBoardStates({ type, initialConfig, onSave, onCancel }) {
  const [config, setConfig] = useState(initialConfig);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setConfig((items) => {
        const oldIndex = items.findIndex(
          (i) => i.order.toString() === active.id,
        );
        const newIndex = items.findIndex((i) => i.order.toString() === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);

        // Update order values
        return reordered.map((item, index) => ({
          ...item,
          order: index,
        }));
      });
    }
  };

  const handleUpdate = (order, updates) => {
    setConfig((items) =>
      items.map((item) =>
        item.order === order ? { ...item, ...updates } : item,
      ),
    );
  };

  const handleRemove = (order) => {
    if (config.length <= 1) return; // Prevent removing last item

    setConfig((items) => {
      const filtered = items.filter((item) => item.order !== order);
      // Reorder
      return filtered.map((item, index) => ({
        ...item,
        order: index,
      }));
    });
  };

  const handleAdd = () => {
    const newOrder = config.length;
    setConfig([
      ...config,
      {
        label: "",
        color: "#cccccc",
        order: newOrder,
      },
    ]);
  };

  const handleRevertToDefault = () => {
    const defaultConfig =
      type === "status" ? DEFAULT_STATUS_CONFIG : DEFAULT_PRIORITY_CONFIG;
    setConfig(JSON.parse(JSON.stringify(defaultConfig))); // Deep copy
  };

  const handleSave = () => {
    // Validate: no empty labels
    const hasEmpty = config.some((item) => !item.label.trim());
    if (hasEmpty) {
      alert("All items must have a label");
      return;
    }

    onSave(config);
  };

  return (
    <div className="configure-board-states">
      <div className="config-header">
        <h3>Configure {type === "status" ? "Statuses" : "Priorities"}</h3>
        <button className="revert-button" onClick={handleRevertToDefault}>
          <span className="material-symbols-outlined">restart_alt</span>
          Revert to Default
        </button>
      </div>

      <div className="config-list">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={config.map((item) => item.order.toString())}
            strategy={verticalListSortingStrategy}
          >
            {config.map((item) => (
              <SortableItem
                key={item.order}
                id={item.order.toString()}
                item={item}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
                canRemove={config.length > 1}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <button className="add-button" onClick={handleAdd}>
        <span className="material-symbols-outlined">add</span>
        Add {type === "status" ? "Status" : "Priority"}
      </button>

      <div className="button-group">
        <button className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="save-button" onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );
}

ConfigureBoardStates.propTypes = {
  type: PropTypes.oneOf(["status", "priority"]).isRequired,
  initialConfig: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ConfigureBoardStates;
